/**
 * Colours of the image viewer: its background, the close / previous / next
 * controls with the image counter, and the image title.
 *
 * The theme sets them for every page (`colors.viewer*`, the title on its
 * `media-title` module). A page can set its own in `page.viewerLook`, which
 * its subpages inherit key by key.
 */

import { getParentId } from '../utils/pagePaths.js';
import { themeValueAtLayer } from './document.js';
import { colorWithAlpha } from './colors.js';

export const VIEWER_THEME_PATHS = {
    background: 'colors.viewerBackground',
    controls: 'colors.viewerControls',
    controlsBackground: 'colors.viewerControlsBackground',
};

export const VIEWER_LOOK_KEYS = ['background', 'controls', 'controlsBackground', 'title', 'titleBackground'];

const COLOR_VALUE = /^[#a-z0-9(),.%\s-]{1,64}$/i;

function cleanColor(value) {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed && COLOR_VALUE.test(trimmed) ? trimmed : null;
}

function cleanOpacity(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : null;
}

/** Only the known keys with usable colours (and the backdrop opacity); null when nothing is left. */
export function cleanViewerLook(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const out = {};
    VIEWER_LOOK_KEYS.forEach((key) => {
        const color = cleanColor(value[key]);
        if (color) out[key] = color;
    });
    const opacity = cleanOpacity(value.titleBackgroundOpacity);
    if (opacity !== null) out.titleBackgroundOpacity = opacity;
    return Object.keys(out).length ? out : null;
}

/** This page's viewer colours, with any key it leaves out taken from the nearest parent that sets it. */
export function pageViewerLook(page, pages) {
    const out = {};
    let current = page || null;
    const seen = new Set();
    while (current && typeof current === 'object') {
        const own = cleanViewerLook(current.viewerLook);
        if (own) {
            Object.entries(own).forEach(([key, value]) => {
                if (!(key in out)) out[key] = value;
            });
        }
        const parentId = getParentId(current);
        if (!parentId || !pages?.[parentId] || seen.has(parentId)) break;
        seen.add(parentId);
        current = pages[parentId];
    }
    return Object.keys(out).length ? out : null;
}

function themeLayer(layer) {
    return !layer || layer === 'desktop' ? 'base' : layer;
}

/** The theme's viewer colours on one screen size. */
export function themeViewerLook(theme, layer) {
    const out = {};
    Object.entries(VIEWER_THEME_PATHS).forEach(([key, path]) => {
        const color = cleanColor(themeValueAtLayer(theme, path, themeLayer(layer)));
        if (color) out[key] = color;
    });
    return out;
}

/** Custom properties for the viewer's root element. */
export function viewerLookVars(look) {
    if (!look) return {};
    const vars = {};
    if (look.background) vars['--color-background'] = look.background;
    if (look.controls) vars['--viewer-controls'] = look.controls;
    if (look.controlsBackground) vars['--viewer-controls-background'] = look.controlsBackground;
    return vars;
}

/** The page's title colours over the theme's title box. */
export function titleWithPageLook(box, look) {
    const opacity = look?.titleBackgroundOpacity;
    if (!look?.title && !look?.titleBackground && opacity == null) return box;
    return {
        ...(box || {}),
        ...(look.title ? { color: look.title } : {}),
        ...(look.titleBackground ? { background: look.titleBackground } : {}),
        ...(opacity != null ? { backgroundOpacity: opacity } : {}),
    };
}

/** Title colours a page sets when the theme leaves the title box alone. */
export function pageTitleStyle(look, box) {
    if (!look?.title && !look?.titleBackground) return null;
    const opacity = look.titleBackgroundOpacity
        ?? (box?.backgroundOpacity != null && box.backgroundOpacity !== '' ? box.backgroundOpacity : 1);
    const background = look.titleBackground ? colorWithAlpha(look.titleBackground, opacity) : null;
    return {
        ...(look.title ? { color: look.title } : {}),
        ...(background ? { backgroundColor: background, padding: '0.35em 0.65em' } : {}),
    };
}

/** Control colours for elements that sit in the viewer. */
export const VIEWER_CONTROL_STYLE = {
    color: 'var(--viewer-controls, var(--color-text))',
};

export const VIEWER_CONTROL_BACKDROP = {
    backgroundColor: 'var(--viewer-controls-background, var(--color-background))',
};
